import { useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import Actions from "./components/Actions";
import HeaderComponent from "../../../../components/ui/ListHeader";
import EditOffer from "./components/EditOffer";
// import searchedInvoices from "./components/SearchedInvoices";
import * as SelectListWorking from "./components/SelectListWorking";
import * as SelectListSaved from "./components/SelectListSaved";

import {
  Table,
  multiSelect,
  SelectList,
} from "../../../../components/Functional";
import { formatSalesPeopleNames } from "../../../../components/Functional/SalesPeopleSplitEditor/salesPeopleSplitEditorUtils";
import { Modal } from "../../../../components/ui";
import { FETCH_OFFERS_LIST as fetchOffersList } from "../../../../graphql/query/Offer";
import { FETCH_LISTS as fetchLists } from "../../../../graphql/query/List";
import { CREATE_LIST as createListMutation } from "../../../../graphql/mutation/List";
import { LIST_TYPES } from "../../../../constants/listTypes";
import { buildNavigationUrl } from "../../../../util/buildNavigationUrl";

const List = props => {
  const navigate = useNavigate();
  const { formik } = props || {};
  const {
    data: { lists: { edges = [] } = {} } = {},
    loading: loadingWorkingList,
  } = useQuery(fetchLists(), {
    variables: {
      input: { typeId: LIST_TYPES.OFFER_WORKING },
    },
    fetchPolicy: "network-only",
  });

  const [createList] = useMutation(createListMutation(), {
    refetchQueries: [
      {
        query: fetchLists(),
        variables: { input: { typeId: LIST_TYPES.OFFER_WORKING } },
        fetchPolicy: "network-only",
      },
    ],
    awaitRefetchQueries: true,
  });

  // Get the first list's ID if it exists (even if empty, the list object will have an id)
  const workingListId = edges?.[0]?.id ?? null;

  // Ensure working list exists - create it if it doesn't
  useEffect(() => {
    if (!loadingWorkingList && !workingListId) {
      createList({
        variables: {
          input: {
            typeId: LIST_TYPES.OFFER_WORKING,
            name: `select_list_working_${Date.now()}`,
          },
        },
      });
    }
  }, [loadingWorkingList, workingListId, createList]);

  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams?.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const listId = listIdParam ? +listIdParam : null;
  const ids = Object.keys(formik?.values?.selected?.ids || {})?.map(item =>
    parseInt(item.replace("_", "")),
  );

  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    let shouldUpdate = false;

    // Remove workingListId if we're viewing a saved list (listId is present)
    if (listId && currentParams.has("workingListId")) {
      currentParams.delete("workingListId");
      shouldUpdate = true;
    }

    // Update workingListId if we're on selectListWorking tab, it's available, and no saved listId
    if (status === "selectListWorking" && workingListId && !listId) {
      if (currentParams.get("workingListId") !== String(workingListId)) {
        currentParams.set("workingListId", workingListId);
        shouldUpdate = true;
      }
    }

    if (shouldUpdate) {
      const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
      // eslint-disable-next-line
      history.pushState(null, null, newUrl);
    }
  }, [workingListId, status, listId]);

  const multiSelectProps = {
    formik,
    label: "Offers",
    name: "selected",
    keyName: "id",
  };

  const actionProps = {
    formik,
    ids,
    listId,
    workingListId,
    onSuccess: () => formik?.resetForm(),
  };

  const handleChange = value => {
    formik?.resetForm();
    if (value === "all") {
      navigate(
        buildNavigationUrl(
          "/offer",
          new URLSearchParams(listId ? { listId } : {}),
        ),
      );
      return;
    }
    let url = `/offer?status=${value}`;

    // For working list tabs, use listId if present (saved list), otherwise use workingListId
    if (value === "selectListWorking" || value === "selectListSaved") {
      // Include listId if present (viewing/editing a saved list), otherwise use workingListId
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
    } else {
      // For "all" and other tabs, preserve saved list IDs (listId) so items can be added to them
      // But don't include working list IDs
      if (listId) {
        url += `&listId=${listId}`;
      }
    }

    navigate(url);
  };

  const GeneralTabActions = () => {
    // Determine target list ID - use saved list ID if present, otherwise use working list ID
    const targetListId =
      listId && !isNaN(listId) ? listId : workingListId || undefined;

    const createListProps = {
      ...actionProps,
      // Use listId if present (saved list), otherwise use workingListId
      ...(targetListId ? { targetListId } : {}),
      onSuccess: () => (
        formik?.resetForm(),
        navigate(
          `/offer?status=selectListWorking${listId ? `&listId=${listId}` : ""}`,
        )
      ),
      typeId: LIST_TYPES.OFFER_WORKING,
    };

    return <SelectList.CreateList {...createListProps} />;
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      { actions: GeneralTabActions, label: "All Offers", name: "all" },
      // { actions: GeneralTabActions, label: 'Searched Invoices', name: 'searched', tableProps: searchedInvoices() },
      {
        actions: SelectListWorking.Actions,
        label: "Select List (Working)",
        name: "selectListWorking",
        tableProps: SelectListWorking.tableProps(actionProps),
      },
      {
        actions: SelectListSaved.Actions,
        label: "Select List (Saved)",
        name: "selectListSaved",
        tableProps: SelectListSaved.tableProps({ formik }),
      },
    ],
  };
  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};

  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "offersV2",
    headers: [
      multiSelect(multiSelectProps),
      {
        label: "Image",
        name:
          status === "selectListWorking"
            ? "item.artwork.imagesSummary.imgT"
            : "artwork.imagesSummary.imgT",
        type: "image",
      },
      {
        label: "Updated At",
        name: status === "selectListWorking" ? "item.updatedAt" : "updatedAt",
        type: "date",
      },
      {
        label: "Date",
        name: status === "selectListWorking" ? "item.date" : "date",
        sortKey: "date",
        type: "date",
      },
      {
        label: "Artist",
        name: status === "selectListWorking" ? "item.artwork" : "artwork",
        type: "custom",
        component: props => (
          <div
            className={`overflow-hidden text-ellipsis text-base font-medium leading-5 text-black`}
            style={{ maxWidth: 250 }}
          >
            {(props?.label?.artists?.map(artist => artist?.name) || [])?.join(
              ", ",
            )}
          </div>
        ),
      },
      {
        label: "Title",
        name:
          status === "selectListWorking"
            ? "item.artwork.title"
            : "artwork.title",
        type: "custom",
        component: props => {
          const offer = props?.row?.item || props?.row || {};
          const modalProps = {
            body: EditOffer,
            closeOnBackdrop: false,
            offer: offer,
            hideCloseButton: false,
            scale: "lg",
          };

          return (
            <Modal {...modalProps}>
              <span className="cursor-pointer underline hover:no-underline">
                {props?.label}
              </span>
            </Modal>
          );
        },
      },
      {
        label: "Status",
        name:
          status === "selectListWorking"
            ? "item.status.description"
            : "status.description",
        sortKey: "statusId",
        type: "label",
      },
      {
        label: "Currency",
        name:
          status === "selectListWorking"
            ? "item.currency.code"
            : "currency.code",
        sortKey: "currencyId",
        type: "label",
      },
      {
        label: "Amount",
        name: status === "selectListWorking" ? "item.amount" : "amount",
        sortKey: "amount",
        type: "custom",
        component: props => {
          const amount =
            status === "selectListWorking"
              ? props?.row?.item?.amount
              : props?.row?.amount;
          const tbiAmount =
            status === "selectListWorking"
              ? props?.row?.item?.tbiAmount
              : props?.row?.tbiAmount;

          return (
            <div className="flex flex-col">
              <span>{amount ?? ""}</span>
              {tbiAmount && (
                <span className="text-sm text-gray-500">({tbiAmount})</span>
              )}
            </div>
          );
        },
      },
      {
        label: "Closed",
        name: status === "selectListWorking" ? "item.dateClosed" : "dateClosed",
        sortKey: "dateClosed",
        type: "date",
      },
      {
        label: "Notes",
        name: status === "selectListWorking" ? "item.notes" : "notes",
        sortKey: "notes",
        type: "label",
        width: "400px",
      },
      {
        label: "Selling People",
        name:
          status === "selectListWorking"
            ? "item.offerSalesPeople"
            : "offerSalesPeople",
        type: "custom",
        component: props =>
          formatSalesPeopleNames(
            status === "selectListWorking"
              ? props?.row?.item?.offerSalesPeople
              : props?.row?.offerSalesPeople,
          ),
      },
      { label: "", type: "custom", component: Actions },
    ],
    inputs: [],
    FETCH_QUERY: fetchOffersList({}),
    variables: {
      limit: 50,
    },
    tabProps,
    ...tabTableProps,
  };

  return (
    <>
      <HeaderComponent
        rightComponent={() => {
          return (
            <div className="flex flex-wrap gap-2 sm:gap-4">
              {tabActions?.(actionProps)}
            </div>
          );
        }}
      />
      <div className="mt-6 w-full">
        <Table {...tableProps} />
      </div>
    </>
  );
};

export default List;
